import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs/internal/Subscription'
import { NgbModal, NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { ZXingScannerComponent } from "@zxing/ngx-scanner";
import { BrowserCodeReader } from '@zxing/browser';
import { NoCameraAccessComponent } from 'src/app/layouts/qr-scanner/no-camera-access/no-camera-access.component';
import { firstValueFrom } from 'rxjs';

interface NormalisedDevice {
    device: MediaDeviceInfo;
    normalisedLabel: string;
    facing: 'Front ' | 'Back ' | '';
    index: number;
}


@Injectable({ providedIn: 'root' })
export class QRScannerService {
    constructor(private _modalService: NgbModal, private router: Router) { }

    public scanner: ZXingScannerComponent;

    public onScan: ((result: string) => void) | null = null;
    public onClose: (() => void) | null = null;

    private _scanSubscription: Subscription = null;

    private _videoElement: HTMLVideoElement;

    public currentDevice: MediaDeviceInfo = null;
    public selectedDevice: string = null;
    public normalisedDevices: NormalisedDevice[] = null;
    public isStreamInitializing: boolean = false;

    public torchAvailable: boolean;
    public torchEnabled: boolean;

    private _availableDevices: MediaDeviceInfo[];

    private _userDefaultCamera: MediaDeviceInfo;
    private _defaultCamera: MediaDeviceInfo;

    private _hasDevices: boolean;
    private _hasPermission: boolean = false;
    
    private _streamError: any;

    private _qrResultString: string = null;

    saveUserDefaultCamera(newUserDefaultCamera: MediaDeviceInfo) {
        localStorage.setItem('defaultCamera', JSON.stringify(newUserDefaultCamera));
    }

    getUserDefaultCamera(): MediaDeviceInfo | null {
        const data = localStorage.getItem('defaultCamera');
        return data ? JSON.parse(data) : null;
    }

    // for debugging purposes
    clearUserDefaultCamera() {
        localStorage.removeItem('defaultCamera');
    }

    clear() {
        this.onScan = null;
        this.onClose = null;
        this._hasDevices = null;
    }

    async initialiseScanner(scanner: ZXingScannerComponent | null) {
        if (scanner) {
            this.scanner = scanner;
        }

        // Check if the service has onScan and onClose callbacks set
        if (!this.onScan || !this.onClose) {
            await this.router.navigate(["/"]);
            return;
        }

        this.closeScanner();
        await new Promise(resolve => setTimeout(resolve, 500));

        this._userDefaultCamera = this.getUserDefaultCamera();

        if (!this._hasDevices) {
            this._hasPermission = await this.askForPermission();
        }

        if (this._hasPermission) {
            const devices = await BrowserCodeReader.listVideoInputDevices() || [];
            this._hasDevices = devices && devices.length > 0 && !!devices[0].deviceId;

            if (this._hasDevices) {
                this.onCamerasFound(devices);
                this.currentDevice =
                    devices.find(d => d.deviceId === this._userDefaultCamera?.deviceId)
                    || this._defaultCamera
                    || devices[devices.length - 1];

                this.selectedDevice = this.currentDevice.deviceId;
                await this.startScanCurrentDevice();

            } else {
                this.noCameraAccess();
                return;
            }
        }
    }

    async startScanCurrentDevice() {
        try {

            if (this.currentDevice) {

                this.isStreamInitializing = true;

                // Start the scanner with the current device
                this._videoElement = this.scanner.previewElemRef.nativeElement;
                if (!this._videoElement) {
                    throw new Error("No video element found in scanner component.");
                }

                let scanStream: any = await this.scanner.codeReader.scanFromDeviceObservable(this.currentDevice.deviceId, this._videoElement);

                // Retry logic for scan stream if it is stopped or has an error
                if (scanStream && scanStream.source.isStopped && scanStream.source.hasError) {
                    scanStream = await this.scanner.codeReader.scanFromDeviceObservable(this.currentDevice.deviceId, this._videoElement);
                }

                if (scanStream && scanStream.source.isStopped && scanStream.source.hasError) {
                    console.warn(scanStream.source.thrownError);
                    this.noCameraAccess();
                    return;
                }

                const next = ((x: any) => {
                    if (x.result) {
                        this.onCodeResult(x.result.text);
                    }
                }).bind(this);
                const error = (err: any) => {
                    if (err.name === "AbortError") {
                        console.warn("AbortError detected, retrying...");
                        setTimeout(async () => await this.startScanCurrentDevice(), 500);
                    } else {
                        console.error("Error in scan stream: ", err);
                    }
                };
                const complete = () => {
                    console.log("Scan stream completed.");
                };

                // Subscribe to the scan stream
                this._scanSubscription = scanStream.subscribe({
                    next,
                    error,
                    complete
                });

                // Check for / set up torch controls
                const controls = this.scanner.codeReader.getScannerControls();
                const hasTorchControl = typeof controls.switchTorch !== 'undefined';
                this.scanner.torchCompatible.next(hasTorchControl);
            } else {
                console.error("No current device set for scanning.");
            }
        } catch (e) {
            console.error(e);
        } finally {
            this.isStreamInitializing = false;
        }
    }

    async changeDevice(selectedValue: string) {

        this.closeScanner();
        await new Promise(resolve => setTimeout(resolve, 500));

        // Set new device
        this.currentDevice = this._availableDevices.find(x => x.deviceId === selectedValue);
        this.selectedDevice = this.currentDevice.deviceId;

        if (!this.currentDevice) {
            console.error("Selected device not found");
        }

        await this.startScanCurrentDevice();
    }

    closeScanner() {
        BrowserCodeReader.releaseAllStreams();
        if (this._scanSubscription) {
            this._scanSubscription.unsubscribe();
            this._scanSubscription = undefined;
        }
    }

    async askForPermission(): Promise<boolean> {
        let stream: MediaStream;
        let permission: boolean = false;

        try {
            stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
            permission = !!stream;
        } catch (err) {
            this._streamError = err;
            console.error("Error requesting camera permission: ", err);
            if (this._streamError.name === "NotAllowedError") {
                this.noCameraAccess();
                this._hasDevices = false;
            }
        } finally {
            if (stream) {
                stream.getTracks().forEach(t => t.stop());
            }
            await new Promise(resolve => setTimeout(resolve, 500));
        }

        return permission;
    }

    onCamerasFound(devices: MediaDeviceInfo[]) {
        this._availableDevices = devices;
        this.normaliseDevices(devices);
        this._defaultCamera = this.getBestBackCamera(devices) || devices[devices.length - 1];
    }

    onCodeResult(resultString: string) {
        this.saveUserDefaultCamera(this.currentDevice);
        this._qrResultString = resultString;
        if (this.onScan) {
            this.onScan(this._qrResultString);
        }
    }

    // Used to standardise device labels 
    normaliseDevices(devices: MediaDeviceInfo[]) {
        this.normalisedDevices = [];
        const facingGroups: { [key: string]: MediaDeviceInfo[] } = { ['Front ']: [], ['Back ']: [], ['']: [] };

        devices.forEach(device => {
            const label = device.label.toLowerCase();
            if (label.includes("camera2") || label.includes("facing")) {
                if (/front|frontal|selfie/.test(label)) {
                    facingGroups['Front '].push(device);
                } else if (/back|rear|environment/.test(label)) {
                    facingGroups['Back '].push(device);
                } else {
                    facingGroups[''].push(device);
                }
            } else {
                this.normalisedDevices.push({
                    device,
                    normalisedLabel: device.label,
                    facing: "",
                    index: null
                });
            }
        });

        (['Front ', 'Back ', ''] as const).forEach(facing => {
            facingGroups[facing].forEach((device, idx) => {
                this.normalisedDevices.push({
                    device,
                    normalisedLabel: `${facing}Camera ${idx + 1}`,
                    facing,
                    index: idx + 1
                });
            });
        });
    }

    getBestBackCamera(devices: MediaDeviceInfo[]): MediaDeviceInfo {
        const backKeywords = /back|rear|environment/i;
        const backDevices = devices.filter(d => backKeywords.test(d.label));

        if (backDevices.length === 0) return undefined;

        for (const device of backDevices) {
            const label = device.label.toLowerCase();
            const camera2Index = label.indexOf("camera2");
            if (camera2Index !== -1) {
                const afterCamera2 = label.substring(camera2Index + "camera2".length);
                if (afterCamera2.includes("0")) {
                    return device;
                }
            }
        }
        return backDevices[0];
    }

    noCameraAccess() {
        let ngbModalOptions: NgbModalOptions = {
            windowClass: "modal-custom"
        };
        const modalRef = this._modalService.open(NoCameraAccessComponent, ngbModalOptions);
    }


    toggleTorch() {
        this.torchEnabled = !this.torchEnabled;
    }

    isTorchCompatible(isCompatible: boolean) {
        this.torchAvailable = isCompatible;
        if (!isCompatible) {
            this.torchEnabled = false;
        }
    }
    
    get isMozillaBrowser(): boolean {
        return navigator.userAgent.toLowerCase().includes('firefox');
    }
}
